/** Pure attachment helpers for the manager chat (P5.5). Kept free of React so they unit-test without a
 * DOM; `ManagerChat.tsx` renders the results.
 *
 * Why extension inference exists: `@livekit/components-react` reconstructs a received attachment as
 * `new File(chunks, info.name)` — with **no** `type` argument — so `file.type` is an EMPTY STRING for
 * every file that arrives over the wire (only the sender's own local echo carries the real type from the
 * OS picker). The library's own `ChatEntry` renders attachments behind `file.type.startsWith('image/')`,
 * which is therefore never true for a received image; that is why the manager chat brings its own entry
 * renderer and resolves the type from the file NAME when `file.type` is blank. */

import {
  CHAT_ATTACHMENT_MAX_COUNT,
  type ChatAttachmentRejection,
  isChatAttachmentMimeType,
  validateChatAttachment,
} from "@evas/shared";

/** Extension → MIME for the shared allowlist. Only these are resolvable; anything else stays unknown
 * (rendered as a plain download link, never as an image).
 *
 * MUST cover every type in `CHAT_ATTACHMENT_MIME_TYPES`: a received file has no MIME type of its own, so
 * an allowlisted type missing here resolves to `""` and renders as nothing — that was the .pptx bug
 * (EVAS-118). Exported so a test can assert that coverage. */
export const EXTENSION_MIME: Readonly<Record<string, string>> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  gif: "image/gif",
  pdf: "application/pdf",
  txt: "text/plain",
  csv: "text/csv",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
};

/** Infer an allowlisted MIME type from a file name, or "" when the extension isn't one we accept. */
export function mimeFromFileName(name: string): string {
  const ext = name.slice(name.lastIndexOf(".") + 1).toLowerCase();
  if (!ext || !name.includes(".")) return "";
  return EXTENSION_MIME[ext] ?? "";
}

/** The effective MIME type of a chat attachment: the file's own type when the browser set one (local
 * echo), else inferred from its name (everything received over the wire — see the module note). */
export function resolveAttachmentType(file: { name: string; type: string }): string {
  const declared = file.type.trim().toLowerCase();
  if (declared && isChatAttachmentMimeType(declared)) return declared;
  return mimeFromFileName(file.name);
}

export interface PickResult {
  /** Files that passed the shared limits, capped at {@link CHAT_ATTACHMENT_MAX_COUNT} including
   * `alreadyPending`. */
  accepted: File[];
  /** Why the first rejected file was rejected, or null when everything passed. */
  rejection: ChatAttachmentRejection | null;
}

/** Filter a freshly picked file list against the shared limits (type, size, and the per-message count
 * given how many are already queued). Pure — the UI turns `rejection` into a message. */
export function pickAttachments(picked: readonly File[], alreadyPending: number): PickResult {
  const accepted: File[] = [];
  let rejection: ChatAttachmentRejection | null = null;
  for (const file of picked) {
    const bad = validateChatAttachment(file);
    if (bad) {
      rejection ??= bad;
    } else if (alreadyPending + accepted.length >= CHAT_ATTACHMENT_MAX_COUNT) {
      rejection ??= "count";
    } else {
      accepted.push(file);
    }
  }
  return { accepted, rejection };
}

/** User-facing text for a rejection (the manager room is en-US only — it has no i18n layer). */
export function rejectionMessage(
  rejection: ChatAttachmentRejection,
  opts: { maxBytes: number; maxCount: number },
): string {
  switch (rejection) {
    case "type":
      return "That file type isn't supported.";
    case "size":
      return `Files must be ${Math.round(opts.maxBytes / (1024 * 1024))} MB or smaller.`;
    case "count":
      return `You can attach at most ${opts.maxCount} files per message.`;
  }
}
